import { useMemo } from "react";

// Soft, varied night-sky palette — warm whites, gold, amber, lavender, pale rose, sky-blue
const STAR_COLORS = [
    { h: "#fefce8", glow: "rgba(254, 252, 232, 0.7)" },   // warm white
    { h: "#fde68a", glow: "rgba(253, 230, 138, 0.7)" },   // pale gold
    { h: "#fcd34d", glow: "rgba(252, 211, 77, 0.7)" },    // warm amber
    { h: "#f0c674", glow: "rgba(240, 198, 116, 0.7)" },   // signature gold
    { h: "#fbcfe8", glow: "rgba(251, 207, 232, 0.65)" },  // soft rose
    { h: "#ddd6fe", glow: "rgba(221, 214, 254, 0.65)" },  // lavender
    { h: "#bfdbfe", glow: "rgba(191, 219, 254, 0.65)" },  // sky blue
    { h: "#a7f3d0", glow: "rgba(167, 243, 208, 0.55)" },  // mint (rare)
];

function pickColor(seed) {
    // Weighted: gold/amber/white most common, others rarer for a peaceful aesthetic
    const weights = [0.18, 0.2, 0.16, 0.18, 0.1, 0.08, 0.07, 0.03];
    let r = seed;
    for (let i = 0; i < STAR_COLORS.length; i++) {
        r -= weights[i];
        if (r <= 0) return STAR_COLORS[i];
    }
    return STAR_COLORS[0];
}

/**
 * Lightweight CSS star field — animated twinkles, multiple colors.
 */
export default function StarField({ density = 80, className = "", interactive = false, points = [] }) {
    const stars = useMemo(() => {
        return Array.from({ length: density }, (_, i) => {
            const size = Math.random() * 2.2 + 0.5;
            const opacity = Math.random() * 0.6 + 0.3;
            const top = Math.random() * 100;
            const left = Math.random() * 100;
            const delay = Math.random() * 5;
            const duration = 3 + Math.random() * 4;
            const c = pickColor(Math.random());
            return { id: i, size, opacity, top, left, delay, duration, color: c };
        });
    }, [density]);

    return (
        <div className={`pointer-events-none overflow-hidden ${className}`} aria-hidden>
            {stars.map((s) => (
                <span
                    key={s.id}
                    className="absolute rounded-full twinkle"
                    style={{
                        width: `${s.size}px`,
                        height: `${s.size}px`,
                        top: `${s.top}%`,
                        left: `${s.left}%`,
                        background: s.color.h,
                        opacity: s.opacity,
                        animationDelay: `${s.delay}s`,
                        animationDuration: `${s.duration}s`,
                        boxShadow: s.size > 1.4 ? `0 0 ${4 + s.size * 2}px ${s.color.glow}` : undefined,
                    }}
                />
            ))}
            {interactive &&
                points.map((p, idx) => {
                    // Explicit color wins, else deterministic hash based on memorial id.
                    let color;
                    if (p.colorHex) {
                        color = { h: p.colorHex, glow: p.colorHex + "B3" };
                    } else {
                        const seed = ((p.id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 100) / 100;
                        color = pickColor(seed);
                    }
                    return (
                        <button
                            type="button"
                            key={p.id || idx}
                            onClick={p.onClick}
                            title={p.title}
                            className="absolute group pointer-events-auto"
                            style={{
                                top: `${(p.y || 0.5) * 100}%`,
                                left: `${(p.x || 0.5) * 100}%`,
                                transform: "translate(-50%, -50%)",
                            }}
                            data-testid={p.testid || `star-${p.id || idx}`}
                        >
                            <span
                                className="block w-3 h-3 rounded-full gentle-pulse"
                                style={{
                                    background: color.h,
                                    boxShadow: `0 0 18px ${color.glow}, 0 0 6px ${color.glow}`,
                                }}
                            />
                            <span className="absolute left-1/2 -translate-x-1/2 top-5 whitespace-nowrap text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity bg-[#040710]/85 px-2 py-1 rounded" style={{ color: color.h }}>
                                {p.title}
                            </span>
                        </button>
                    );
                })}
        </div>
    );
}
